import React, { useState, useMemo } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Block, PrixUnitaireSourceItem } from '../../types';
import { BlockCalculationService } from '../../services';

interface PuSourcesModalProps {
  block: Block;
  onClose: () => void;
  onUpdate: (updates: Partial<Block>) => void;
}

const PuSourcesModal: React.FC<PuSourcesModalProps> = ({ block, onClose, onUpdate }) => {
  const [sources, setSources] = useState<PrixUnitaireSourceItem[]>(
    block.content.prixUnitaireSources ?? []
  );

  const activeSources = useMemo(() => sources.filter(s => s.active), [sources]);

  const stats = useMemo(() => {
    const prices = activeSources.map(s => s.prixUnitaire).sort((a, b) => a - b);
    if (prices.length === 0) return null;
    const sum = prices.reduce((a, b) => a + b, 0);
    const mid = Math.floor(prices.length / 2);
    return {
      min: prices[0],
      max: prices[prices.length - 1],
      avg: sum / prices.length,
      median: prices.length % 2 === 0 ? (prices[mid - 1] + prices[mid]) / 2 : prices[mid],
      count: prices.length,
    };
  }, [activeSources]);

  const handleToggle = (id: number) => {
    setSources(prev => prev.map(s => s.id === id ? { ...s, active: !s.active } : s));
  };

  const handleApply = () => {
    if (!stats) return;
    const qty = BlockCalculationService.parseQuantity(block.content.quantite ?? '1');
    onUpdate({
      content: {
        ...block.content,
        prixUnitaire: `${BlockCalculationService.formatPrice(stats.avg)} €`,
        prixTotal: BlockCalculationService.formatPrice(stats.avg * qty),
        prixTotalManual: false,
        prixUnitaireReliability: undefined,
        prixUnitaireExpertCurated: true,
        prixUnitaireSources: sources,
      },
    });
    onClose();
  };

  const fmt = (n: number) => BlockCalculationService.formatPrice(n);
  const unite = block.content.unite ?? '';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[680px] max-h-[80vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Sources du prix unitaire</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-sm">
              {block.content.intitule || block.content.titre}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-3 bg-violet-50 border-b border-violet-100 flex items-center gap-8">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-0.5">Sélectionnés</div>
            <div className="text-sm font-semibold text-violet-700">
              {activeSources.length} / {sources.length}
            </div>
          </div>
          {stats ? (
            <>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Min</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.min)} €</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Max</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.max)} €</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Médiane</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.median)} €</div>
              </div>
              <div className="flex-1 text-right">
                <div className="text-xs text-gray-500 mb-0.5">Nouveau PU (moyenne)</div>
                <div className="text-lg font-bold text-violet-700">
                  {fmt(stats.avg)} €/{unite}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 text-center text-sm text-gray-400 italic">
              Sélectionnez au moins un projet
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
              <tr>
                <th className="w-10 px-4 py-2" />
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Chantier</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Localisation</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Fournisseur</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Année</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">PU</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr
                  key={s.id}
                  onClick={() => handleToggle(s.id)}
                  className={`cursor-pointer border-b border-gray-50 transition-colors ${
                    s.active
                      ? 'bg-white hover:bg-violet-50/40'
                      : 'bg-gray-50/60 hover:bg-gray-100/60'
                  }`}
                >
                  <td className="px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={s.active}
                      onChange={() => handleToggle(s.id)}
                      onClick={e => e.stopPropagation()}
                      className="w-4 h-4 text-violet-600 rounded border-gray-300 focus:ring-violet-500 cursor-pointer"
                    />
                  </td>
                  <td className={`px-4 py-2.5 font-medium ${s.active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                    {s.nom}
                  </td>
                  <td className={`px-4 py-2.5 ${s.active ? 'text-gray-500' : 'text-gray-300'}`}>
                    {s.localisation}
                  </td>
                  <td className={`px-4 py-2.5 text-xs ${s.active ? 'text-gray-400' : 'text-gray-300'}`}>
                    {s.fournisseur}
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${s.active ? 'text-gray-500' : 'text-gray-300'}`}>
                    {s.annee}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${s.active ? 'text-violet-600' : 'text-gray-300'}`}>
                    {fmt(s.prixUnitaire)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sources.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">
              Aucune source disponible pour ce prix unitaire
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Décochez les projets aberrants pour affiner le prix moyen calculé
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleApply}
              disabled={!stats || stats.count === 0}
              className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Appliquer — {stats ? `${fmt(stats.avg)} €` : '—'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PuSourcesModal;
